import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react'
import { AppState } from '../types/recipe'

interface AppContextType {
  state: AppState
  setSearchQuery: (query: string) => void
  setActiveCategory: (category: string | null) => void
  setScrollPosition: (position: number) => void
  toggleFavorite: (slug: string) => void
  setLanguage: (lang: string) => void
  toggleOpenDyslexic: () => void
  toggleHighContrast: () => void
}

const AppContext = createContext<AppContextType | undefined>(undefined)

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AppState>({
    searchQuery: '',
    activeCategory: null,
    scrollPosition: 0,
    favorites: [],
    language: 'fr',
    useOpenDyslexic: false,
    useHighContrast: false,
  })

  // Load language and favorites from localStorage on mount
  useEffect(() => {
    const savedFavs = localStorage.getItem('favorites')
    const savedLang = localStorage.getItem('language')
    const savedDyslexic = localStorage.getItem('useOpenDyslexic')
    const savedHighContrast = localStorage.getItem('useHighContrast')
    
    let favorites = []
    if (savedFavs) {
      try {
        const parsed = JSON.parse(savedFavs)
        if (Array.isArray(parsed)) favorites = parsed
      } catch (e) {}
    }

    let language = 'fr'
    if (savedLang) {
      language = savedLang
    } else if (typeof navigator !== 'undefined') {
      const browserLang = navigator.language.split('-')[0]
      if (['en', 'fr'].includes(browserLang)) {
        language = browserLang
      }
    }

    const useOpenDyslexic = savedDyslexic === 'true'
    const useHighContrast = savedHighContrast === 'true'

    setState((prev) => ({ ...prev, favorites, language, useOpenDyslexic, useHighContrast }))
  }, [])

  useEffect(() => {
    if (typeof document !== 'undefined') {
      const root = document.documentElement
      if (state.useOpenDyslexic) {
        root.classList.add('font-dyslexic')
      } else {
        root.classList.remove('font-dyslexic')
      }
    }
  }, [state.useOpenDyslexic])

  useEffect(() => {
    if (typeof document !== 'undefined') {
      const root = document.documentElement
      if (state.useHighContrast) {
        root.classList.add('high-contrast')
      } else {
        root.classList.remove('high-contrast')
      }
    }
  }, [state.useHighContrast])

  const toggleOpenDyslexic = () => {
    setState((prev) => {
      const newVal = !prev.useOpenDyslexic
      localStorage.setItem('useOpenDyslexic', String(newVal))
      return { ...prev, useOpenDyslexic: newVal }
    })
  }

  const toggleHighContrast = () => {
    setState((prev) => {
      const newVal = !prev.useHighContrast
      localStorage.setItem('useHighContrast', String(newVal))
      return { ...prev, useHighContrast: newVal }
    })
  }

  const setLanguage = (language: string) => {
    localStorage.setItem('language', language)
    setState((prev) => ({ ...prev, language }))
  }

  const setSearchQuery = (searchQuery: string) => {
    setState((prev) => ({ 
      ...prev, 
      searchQuery, 
      scrollPosition: 0,
      // Clear active category if a search query is entered
      activeCategory: searchQuery !== '' ? null : prev.activeCategory
    }))
  }

  const setActiveCategory = (activeCategory: string | null) => {
    setState((prev) => ({ 
      ...prev, 
      activeCategory, 
      scrollPosition: 0
    }))
  }

  const setScrollPosition = (scrollPosition: number) => {
    setState((prev) => ({ ...prev, scrollPosition }))
  }

  const toggleFavorite = (slug: string) => {
    setState((prev) => {
      // Slugs are shared between fr/en, so this automatically syncs both
      const isFavorite = prev.favorites.includes(slug)
      const newFavorites = isFavorite
        ? prev.favorites.filter((s) => s !== slug)
        : [...prev.favorites, slug]
      
      localStorage.setItem('favorites', JSON.stringify(newFavorites))
      return { ...prev, favorites: newFavorites }
    })
  }

  return (
    <AppContext.Provider
      value={{ 
        state, 
        setSearchQuery, 
        setActiveCategory, 
        setScrollPosition,
        toggleFavorite,
        setLanguage,
        toggleOpenDyslexic,
        toggleHighContrast
      }}
    >
      {children}
    </AppContext.Provider>
  )
}

export const useAppContext = () => {
  const context = useContext(AppContext)
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider')
  }
  return context
}
